import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import '../styles/AdminDashboard.css';

interface MenuSummary {
  [key: string]: number;
}

interface ApartmentSummary {
  apartment_name: string;
  apartment_address: string | null;
  entrance_password?: string | null;
  total_count: number;
  total_house_count?: number;
  menu_summary: MenuSummary;
  chart_id: number;
  completed_count: number;
  is_all_completed: boolean;
  cooler_bag_count: number;
  is_personal_delivery?: boolean;
  first_completed_at?: string | null;
  last_completed_at?: string | null;
  assigned_driver_id?: number | null;
  assigned_driver_name?: string | null;
  distribution_group_label?: string | null;
  distribution_group_index?: number | null;
}

interface DashboardData {
  delivery_date: string;
  total_apartments: number;
  apartments: ApartmentSummary[];
}

interface ExclusionSchedule {
  id: number;
  apartment_name: string;
  dong: string;
  ho: string;
  customer_name: string;
  start_date: string;
  end_date: string;
  reason?: string;
  created_by?: string;
  created_at?: string;
}

interface DistributionStatus {
  chart_id: number;
  apartment_name: string;
  menu_checks: { [key: string]: boolean };
  is_all_distributed: boolean;
}

interface DefaultApartmentOrder {
  apartment_name: string;
  display_order: number;
}

interface AutoRegistrationConfig {
  enabled: boolean;
  run_time: string;
  apply_adjustments: boolean;
  stop_on_mismatch: boolean;
  notify_on_success: boolean;
  notify_on_skip: boolean;
  source_url?: string;
}

interface AutoRegistrationRun {
  id: number;
  trigger_type: string;
  status: string;
  source_delivery_date: string | null;
  source_summary: MenuSummary;
  live_summary: MenuSummary;
  result_summary: Record<string, unknown>;
  message: string | null;
  started_at: string | null;
  finished_at: string | null;
}

interface AutoRegistrationProgress {
  phase_label?: string;
  current?: number;
  total?: number;
  current_apartment?: string;
}

interface DiscordSettings {
  webhook_url: string;
  enabled: boolean;
}

const AUTO_REGISTRATION_DEFAULT_CONFIG: AutoRegistrationConfig = {
  enabled: true,
  run_time: '07:00',
  apply_adjustments: true,
  stop_on_mismatch: true,
  notify_on_success: false,
  notify_on_skip: false,
  source_url: 'https://carryhealcount.duckdns.org/',
};

const CORE_SUMMARY_ITEMS: Array<[string, string]> = [
  ['total_count', '총 개수'],
  ['korean_1_count', '한식 1인'],
  ['korean_2_count', '한식 2인'],
  ['simple_count', '간편식'],
  ['salad_count', '샐러드'],
  ['sandwich_count', '샌드위치'],
];

const AUTO_REGISTRATION_STATUS_LABELS: Record<string, string> = {
  success: '성공',
  partial_failure: '부분 실패',
  failed: '실패',
  mismatch: '합계 불일치',
  dry_run_success: '점검 성공',
  dry_run_partial_failure: '점검 부분 실패',
  skipped_existing_charts: '기존 차트 존재',
  skipped_no_breakfast: '조식 없음',
  skipped_already_done: '이미 실행됨',
  running: '실행 중',
};

const RETRYABLE_AUTO_REGISTRATION_STATUSES = new Set([
  'failed',
  'mismatch',
  'partial_failure',
  'dry_run_partial_failure',
]);

const getAutoRegistrationProgress = (run?: AutoRegistrationRun | null): AutoRegistrationProgress | null => {
  const raw = run?.result_summary?.progress;
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return null;
  }
  return raw as AutoRegistrationProgress;
};

const describeAutoRegistrationProgress = (run?: AutoRegistrationRun | null): string => {
  if (!run) {
    return '';
  }

  const progress = getAutoRegistrationProgress(run);
  const label = String(progress?.phase_label || '실행 중');
  const current = Number(progress?.current || 0);
  const total = Number(progress?.total || 0);
  const apartment = String(progress?.current_apartment || '').trim();
  const counts = [
    `성공 ${Number(run.result_summary?.success_count || 0)}개`,
    `데이터없음 ${Number(run.result_summary?.no_data_count || 0)}개`,
    `실패 ${Number(run.result_summary?.fail_count || 0)}개`,
  ].join(' / ');

  const position = total > 0 ? ` (${Math.min(current, total)}/${total})` : '';
  const target = apartment ? ` · ${apartment}` : '';
  return `${label}${position}${target} · ${counts}`;
};

const sortApartmentsByDistributionGroup = (
  apartments: ApartmentSummary[],
  getOrderValue?: (apartment: ApartmentSummary, originalIndex: number) => number,
) => {
  return apartments
    .map((apartment, originalIndex) => ({ apartment, originalIndex }))
    .sort((a, b) => {
      const aGroup = Number.isFinite(Number(a.apartment.distribution_group_index))
        ? Number(a.apartment.distribution_group_index)
        : 9999;
      const bGroup = Number.isFinite(Number(b.apartment.distribution_group_index))
        ? Number(b.apartment.distribution_group_index)
        : 9999;
      const groupDiff = aGroup - bGroup;
      if (groupDiff !== 0) {
        return groupDiff;
      }

      const personalDiff =
        Number(Boolean(a.apartment.is_personal_delivery)) -
        Number(Boolean(b.apartment.is_personal_delivery));
      if (personalDiff !== 0) {
        return personalDiff;
      }

      if (getOrderValue) {
        const orderDiff =
          getOrderValue(a.apartment, a.originalIndex) -
          getOrderValue(b.apartment, b.originalIndex);
        if (orderDiff !== 0) {
          return orderDiff;
        }
      }

      return a.originalIndex - b.originalIndex;
    })
    .map(({ apartment }) => apartment);
};

// 드래그 가능한 아파트 카드 컴포넌트
interface SortableApartmentCardProps {
  apartment: ApartmentSummary;
  sortedMenu: [string, number][];
  onCardClick: (apartmentName: string) => void;
  isDragMode: boolean;
  menuChecks: { [key: string]: boolean };
  onMenuCheck: (chartId: number, apartmentName: string, menuName: string, isChecked: boolean) => void;
  isAllDistributed: boolean;
}

const getMenuToneClass = (menu: string) => {
  const normalized = String(menu || '').replace(/\s+/g, '');
  if (normalized.includes('한식') && normalized.includes('1')) {
    return 'menu-korean-one';
  }
  if (normalized.includes('한식') && normalized.includes('2')) {
    return 'menu-korean-two';
  }
  if (normalized.includes('간편식')) {
    return 'menu-simple';
  }
  if (normalized.includes('샐러드') || normalized.includes('그린식')) {
    return 'menu-salad';
  }
  if (normalized.includes('샌드위치')) {
    return 'menu-sandwich';
  }
  return 'menu-default';
};

const SortableApartmentCard: React.FC<SortableApartmentCardProps> = ({
  apartment,
  sortedMenu,
  onCardClick,
  isDragMode,
  menuChecks,
  onMenuCheck,
  isAllDistributed,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ 
    id: apartment.chart_id.toString(),
    disabled: !isDragMode, // 드래그 모드가 아니면 드래그 비활성화
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    cursor: isDragMode ? (isDragging ? 'grabbing' : 'grab') : 'pointer',
    zIndex: isDragging ? 1000 : 1,
  };

  const isCompleted = apartment.is_all_completed;
  const totalHouseCount = apartment.total_house_count ?? apartment.total_count;
  const distributionGroupLabel = apartment.distribution_group_label || '미배정';
  const distributionGroupIndex = Number.isFinite(Number(apartment.distribution_group_index))
    ? Number(apartment.distribution_group_index)
    : 9999;
  const groupToneClass = distributionGroupIndex >= 9999
    ? 'tone-unassigned'
    : `tone-${distributionGroupIndex % 8}`;
  const formatDeliveryTime = (value?: string | null) => {
    if (!value) return '-';
    const match = String(value).match(/\d{2}:\d{2}/);
    return match ? match[0] : value;
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...(isDragMode ? attributes : {})}
      {...(isDragMode ? listeners : {})}
      className={`apartment-card ${groupToneClass} ${isDragMode ? 'sortable' : ''} ${isDragging ? 'dragging' : ''} ${isCompleted ? 'completed' : ''} ${isAllDistributed ? 'distributed' : ''} ${apartment.is_personal_delivery ? 'personal' : ''}`}
      onClick={(e) => {
        // 체크박스 클릭은 카드 클릭 이벤트에서 제외
        if ((e.target as HTMLElement).closest('.menu-checkbox')) {
          return;
        }
        if (!isDragMode && !isDragging) {
          onCardClick(apartment.apartment_name);
        }
      }}
    >
      {/* 분배 완료 배지 */}
      {isAllDistributed && (
        <div className="distribution-badge">
          ✓ 분배완료
        </div>
      )}
      
      <div className="apartment-header">
        <div className="apartment-title">
          <h3>{apartment.apartment_name}</h3>
          {apartment.is_personal_delivery && (
            <span className="personal-badge">개인</span>
          )}
        </div>
        <span className="total-badge">
          총수량 {apartment.total_count}건
        </span>
      </div>
      
      {apartment.apartment_address && (
        <p className="apartment-address">{apartment.apartment_address}</p>
      )}
      {apartment.entrance_password && (
        <p className="apartment-address">공동현관: {apartment.entrance_password}</p>
      )}
      <div className="apartment-meta">
        <span className="apartment-meta-item">집 수 {totalHouseCount}가구</span>
        <span className="apartment-meta-item">완료 {apartment.completed_count}/{totalHouseCount}</span>
      </div>

      <div className="delivery-time-summary">
        <span>첫 배송 {formatDeliveryTime(apartment.first_completed_at)}</span>
        <span>최근 배송 {formatDeliveryTime(apartment.last_completed_at)}</span>
      </div>
      
      <div className="menu-summary">
        {sortedMenu.map(([menu, count]) => (
          <div 
            key={menu} 
            className={`menu-item ${getMenuToneClass(menu)} ${menuChecks[menu] ? 'checked' : ''}`}
            onClick={(e) => {
              e.stopPropagation();
              onMenuCheck(apartment.chart_id, apartment.apartment_name, menu, !menuChecks[menu]);
            }}
          >
            <div className="menu-checkbox">
              <input
                type="checkbox"
                checked={menuChecks[menu] || false}
                readOnly
              />
              <span className="checkmark"></span>
            </div>
            <span className="menu-label">{menu}</span>
            <span className="menu-value">{count}개</span>
          </div>
        ))}
      </div>
      
      {apartment.cooler_bag_count > 0 && (
        <div className="cooler-bag-badge">
          보냉가방: {apartment.cooler_bag_count}개
        </div>
      )}
      
      <div className="card-footer">
        <span
          className={`distribution-group-badge ${groupToneClass}`}
          title={apartment.assigned_driver_name || distributionGroupLabel}
        >
          {isDragging ? '이동 중' : distributionGroupLabel}
        </span>
      </div>
    </div>
  );
};

const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // 내일 날짜를 기본값으로 설정 (조식은 다음날 배송)
  const getTomorrow = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  };
  
  const [selectedDate, setSelectedDate] = useState<string>(getTomorrow());
  const [sortedApartments, setSortedApartments] = useState<ApartmentSummary[]>([]);
  const [isDragMode, setIsDragMode] = useState(false); // 드래그 모드 ON/OFF
  
  // 제거 예약 관리 관련 상태
  const [showExclusionManager, setShowExclusionManager] = useState(false);
  const [exclusionSchedules, setExclusionSchedules] = useState<ExclusionSchedule[]>([]);
  const [exclusionLoading, setExclusionLoading] = useState(false);
  
  // 분배 체크 상태
  const [distributionStatus, setDistributionStatus] = useState<{ [chartId: number]: DistributionStatus }>({});
  
  // 순서 저장 관련 상태
  const [defaultOrderLoaded, setDefaultOrderLoaded] = useState(false);
  const [showAdminMenu, setShowAdminMenu] = useState(false);
  const adminMenuRef = useRef<HTMLDivElement | null>(null);
  const [showAutoRegistrationManager, setShowAutoRegistrationManager] = useState(false);
  const [autoRegistrationConfig, setAutoRegistrationConfig] = useState<AutoRegistrationConfig>(AUTO_REGISTRATION_DEFAULT_CONFIG);
  const [autoRegistrationNextRunTime, setAutoRegistrationNextRunTime] = useState<string | null>(null);
  const [autoRegistrationIsRunning, setAutoRegistrationIsRunning] = useState(false);
  const [autoRegistrationRuns, setAutoRegistrationRuns] = useState<AutoRegistrationRun[]>([]);
  const [autoRegistrationLoading, setAutoRegistrationLoading] = useState(false);
  const [autoRegistrationSaving, setAutoRegistrationSaving] = useState(false);
  const [autoRegistrationExecuting, setAutoRegistrationExecuting] = useState(false);
  const [autoRegistrationMessage, setAutoRegistrationMessage] = useState<string | null>(null);
  const [discordSettings, setDiscordSettings] = useState<DiscordSettings>({ webhook_url: '', enabled: false });
  const [discordSaving, setDiscordSaving] = useState(false);
  const [discordTesting, setDiscordTesting] = useState(false);
  const [discordMessage, setDiscordMessage] = useState<string | null>(null);
  
  
  // 드래그 앤 드롭 센서 설정
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // API 베이스 URL
  const getApiBase = () => {
    const hostname = window.location.hostname;
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return 'http://localhost:8000';
    }
    if (window.location.protocol === 'https:') {
      return window.location.origin;
    }
    return `http://${hostname}:8000`;
  };

  useEffect(() => {
    fetchDashboard();
    fetchDistributionStatus();
  }, [selectedDate]);

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (adminMenuRef.current && !adminMenuRef.current.contains(event.target as Node)) {
        setShowAdminMenu(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  // 기본 순서 로드 (앱 시작 시 한 번만)
  useEffect(() => {
    if (!defaultOrderLoaded) {
      fetchDefaultApartmentOrder();
    }
  }, [defaultOrderLoaded]);

  // dashboardData가 변경되면 커스텀 순서 적용
  useEffect(() => {
    if (dashboardData && dashboardData.apartments.length > 0) {
      // 1. 먼저 로컬 저장된 순서 확인 (날짜별)
      const savedOrder = localStorage.getItem(`apartment-order-${selectedDate}`);
      if (savedOrder) {
        try {
          const orderMap = JSON.parse(savedOrder);
          const sorted = sortApartmentsByDistributionGroup(
            dashboardData.apartments,
            (apartment, originalIndex) => orderMap[apartment.chart_id] ?? (1000 + originalIndex),
          );
          setSortedApartments(sorted);
          return;
        } catch {
          // 파싱 실패 시 기본 순서 사용
        }
      }
      
      // 2. 날짜별 순서가 없으면 기본 순서(DB) 적용
      const defaultOrder = localStorage.getItem('default-apartment-order');
      if (defaultOrder) {
        try {
          const orderMap: { [name: string]: number } = JSON.parse(defaultOrder);
          const sorted = sortApartmentsByDistributionGroup(
            dashboardData.apartments,
            (apartment, originalIndex) => orderMap[apartment.apartment_name] ?? (1000 + originalIndex),
          );
          setSortedApartments(sorted);
          return;
        } catch {
          // 파싱 실패 시 원래 순서 사용
        }
      }
      
      // 3. 둘 다 없으면 원래 순서 사용
      setSortedApartments(sortApartmentsByDistributionGroup(dashboardData.apartments));
    }
  }, [dashboardData, selectedDate]);

  const fetchDashboard = async () => {
    try {
      // 중요: 새 데이터를 가져오기 전에 이전 데이터 초기화
      // 이전 날짜 데이터가 잘못 표시되는 것을 방지
      setDashboardData(null);
      setSortedApartments([]);
      setDistributionStatus({});
      
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      const response = await fetch(
        `${getApiBase()}/api/admin/dashboard/${selectedDate}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (response.status === 403) {
        setError('관리자 권한이 필요합니다.');
        return;
      }

      if (!response.ok) {
        throw new Error('대시보드 데이터 로드 실패');
      }

      const data = await response.json();
      setDashboardData(data);
    } catch (err) {
      console.error('대시보드 로드 실패:', err);
      setError('대시보드 데이터를 불러올 수 없습니다.');
    } finally {
      setLoading(false);
    }
  };

  /**
   * 분배 상태 조회
   */
  const fetchDistributionStatus = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      const response = await fetch(
        `${getApiBase()}/api/distribution/all-status/${selectedDate}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        const data: DistributionStatus[] = await response.json();
        const statusMap: { [chartId: number]: DistributionStatus } = {};
        data.forEach(status => {
          statusMap[status.chart_id] = status;
        });
        setDistributionStatus(statusMap);
      }
    } catch (err) {
      console.error('분배 상태 조회 실패:', err);
    }
  };

  /**
   * 기본 아파트 순서 로드
   */
  const fetchDefaultApartmentOrder = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      const response = await fetch(
        `${getApiBase()}/api/admin/apartment-order`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        const data: DefaultApartmentOrder[] = await response.json();
        const orderMap: { [name: string]: number } = {};
        data.forEach(item => {
          orderMap[item.apartment_name] = item.display_order;
        });
        localStorage.setItem('default-apartment-order', JSON.stringify(orderMap));
        setDefaultOrderLoaded(true);
      }
    } catch (err) {
      console.error('기본 순서 로드 실패:', err);
    }
  };

  /**
   * 메뉴 분배 체크 업데이트
   */
  const handleMenuCheck = async (chartId: number, apartmentName: string, menuName: string, isChecked: boolean) => {
    const token = localStorage.getItem('token');
    if (!token) return;

    // 낙관적 업데이트 (UI 먼저 반영)
    setDistributionStatus(prev => {
      const current = prev[chartId] || { chart_id: chartId, apartment_name: apartmentName, menu_checks: {}, is_all_distributed: false };
      const newMenuChecks = { ...current.menu_checks, [menuName]: isChecked };
      
      // 모든 메뉴가 체크되었는지 확인
      const apt = sortedApartments.find(a => a.chart_id === chartId);
      const allMenus = apt ? Object.keys(apt.menu_summary) : [];
      const isAllDistributed = allMenus.length > 0 && allMenus.every(menu => newMenuChecks[menu]);
      
      return {
        ...prev,
        [chartId]: {
          ...current,
          menu_checks: newMenuChecks,
          is_all_distributed: isAllDistributed,
        }
      };
    });

    try {
      const response = await fetch(
        `${getApiBase()}/api/distribution/check`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            chart_id: chartId,
            apartment_name: apartmentName,
            delivery_date: selectedDate,
            menu_name: menuName,
            is_checked: isChecked,
          }),
        }
      );

      if (!response.ok) {
        console.error('분배 체크 업데이트 실패');
        // 실패 시 다시 조회
        fetchDistributionStatus();
      }
    } catch (err) {
      console.error('분배 체크 업데이트 실패:', err);
      fetchDistributionStatus();
    }
  };

  /**
   * 현재 순서를 기본 순서로 저장
   */
  const saveAsDefaultOrder = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;

    if (!window.confirm('현재 순서를 기본 순서로 저장하시겠습니까?\n\n이 순서가 앞으로 모든 날짜에 기본 적용됩니다.')) {
      return;
    }

    try {
      const orders = sortedApartments.map((apt, index) => ({
        apartment_name: apt.apartment_name,
        display_order: index,
      }));

      const response = await fetch(
        `${getApiBase()}/api/admin/apartment-order`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ orders }),
        }
      );

      if (response.ok) {
        // 로컬 스토리지도 업데이트
        const orderMap: { [name: string]: number } = {};
        orders.forEach(item => {
          orderMap[item.apartment_name] = item.display_order;
        });
        localStorage.setItem('default-apartment-order', JSON.stringify(orderMap));
        
        alert('✅ 기본 순서가 저장되었습니다!');
      } else {
        alert('❌ 순서 저장에 실패했습니다.');
      }
    } catch (err) {
      console.error('순서 저장 실패:', err);
      alert('❌ 네트워크 오류가 발생했습니다.');
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setSortedApartments((items) => {
        const oldIndex = items.findIndex(item => item.chart_id.toString() === active.id);
        const newIndex = items.findIndex(item => item.chart_id.toString() === over.id);
        
        const movedOrder = arrayMove(items, oldIndex, newIndex);
        const newOrder = sortApartmentsByDistributionGroup(
          movedOrder,
          (_apartment, originalIndex) => originalIndex,
        );
        
        // 순서를 localStorage에 저장
        const orderMap: { [key: number]: number } = {};
        newOrder.forEach((apt, index) => {
          orderMap[apt.chart_id] = index;
        });
        localStorage.setItem(`apartment-order-${selectedDate}`, JSON.stringify(orderMap));
        
        return newOrder;
      });
    }
  };

  const handleApartmentClick = (apartmentName: string) => {
    // 확인 대화상자 표시
    if (window.confirm(`${apartmentName} 배송차트를 확인하시겠습니까?`)) {
      navigate(`/chart/${encodeURIComponent(apartmentName)}/${selectedDate}`);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('driverInfo');
    navigate('/login');
  };

  const handleCleanupOldData = async () => {
    if (!window.confirm('3개월 이상 된 배송 완료 기록과 출근 확인 기록,\n6개월 이상 된 배송 차트를 삭제하시겠습니까?\n\n※ 삭제된 데이터는 복구할 수 없습니다.')) {
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      const response = await fetch(`${getApiBase()}/api/admin/cleanup-old-data`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        alert(`✅ 데이터 정리 완료!\n\n` +
              `배송 완료 기록: ${data.deleted.completed_deliveries}건 삭제\n` +
              `출근 확인 기록: ${data.deleted.delivery_confirmations}건 삭제\n` +
              `배송 차트: ${data.deleted.delivery_charts}건 삭제`);
        
        // 대시보드 새로고침
        fetchDashboard();
      } else {
        const errorData = await response.json();
        alert(`❌ 데이터 정리 실패\n${errorData.detail || ''}`);
      }
    } catch (err) {
      console.error('데이터 정리 실패:', err);
      alert('네트워크 오류가 발생했습니다.');
    }
  };

  const formatDateTime = (value: string | null | undefined) => {
    if (!value) {
      return '미정';
    }

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return value;
    }

    return parsed.toLocaleString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatSummary = (summary?: MenuSummary | null) => {
    if (!summary) {
      return '합계 정보 없음';
    }

    const parts = CORE_SUMMARY_ITEMS
      .filter(([key]) => Number(summary[key] || 0) > 0)
      .map(([key, label]) => `${label} ${Number(summary[key] || 0)}개`);

    if (parts.length === 0) {
      return '총 개수 0개';
    }

    return parts.join(' / ');
  };

  const getAutoRegistrationStatusLabel = (status: string) => {
    return AUTO_REGISTRATION_STATUS_LABELS[status] || status;
  };

  const fetchAutoRegistrationConfig = async (showAlertOnError = false) => {
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      const response = await fetch(`${getApiBase()}/api/admin/auto-registration/config`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('자동등록 설정 조회 실패');
      }

      const data = await response.json();
      setAutoRegistrationConfig({
        ...AUTO_REGISTRATION_DEFAULT_CONFIG,
        ...(data.config || {}),
      });
      setAutoRegistrationNextRunTime(data.next_run_time || null);
      setAutoRegistrationIsRunning(Boolean(data.is_running));
    } catch (err) {
      console.error('자동등록 설정 조회 실패:', err);
      if (showAlertOnError) {
        alert('자동등록 설정을 불러오지 못했습니다.');
      }
    }
  };

  const fetchAutoRegistrationRuns = async (showAlertOnError = false) => {
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      const response = await fetch(`${getApiBase()}/api/admin/auto-registration/runs?limit=10`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('자동등록 실행 이력 조회 실패');
      }

      const data = await response.json();
      setAutoRegistrationRuns(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('자동등록 실행 이력 조회 실패:', err);
      if (showAlertOnError) {
        alert('자동등록 실행 이력을 불러오지 못했습니다.');
      }
    }
  };

  const fetchDiscordSettings = async (showAlertOnError = false) => {
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      const response = await fetch(`${getApiBase()}/api/discord-settings`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Discord 설정 조회 실패');
      }

      const data = await response.json();
      setDiscordSettings({
        webhook_url: String(data.webhook_url || ''),
        enabled: Boolean(data.enabled),
      });
    } catch (err) {
      console.error('Discord 설정 조회 실패:', err);
      if (showAlertOnError) {
        alert('Discord 설정을 불러오지 못했습니다.');
      }
    }
  };

  const openAutoRegistrationManager = async () => {
    setShowAutoRegistrationManager(true);
    setAutoRegistrationMessage(null);
    setDiscordMessage(null);
    setAutoRegistrationLoading(true);
    try {
      await Promise.all([
        fetchAutoRegistrationConfig(true),
        fetchAutoRegistrationRuns(true),
        fetchDiscordSettings(true),
      ]);
    } finally {
      setAutoRegistrationLoading(false);
    }
  };

  const handleAutoRegistrationConfigChange = (
    field: keyof AutoRegistrationConfig,
    value: boolean | string,
  ) => {
    setAutoRegistrationConfig((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const saveAutoRegistrationConfig = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;

    setAutoRegistrationSaving(true);
    setAutoRegistrationMessage(null);
    try {
      const response = await fetch(`${getApiBase()}/api/admin/auto-registration/config`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          enabled: autoRegistrationConfig.enabled,
          run_time: autoRegistrationConfig.run_time,
          apply_adjustments: autoRegistrationConfig.apply_adjustments,
          stop_on_mismatch: autoRegistrationConfig.stop_on_mismatch,
          notify_on_success: autoRegistrationConfig.notify_on_success,
          notify_on_skip: autoRegistrationConfig.notify_on_skip,
        }),
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.detail || '자동등록 설정 저장 실패');
      }

      setAutoRegistrationConfig({
        ...AUTO_REGISTRATION_DEFAULT_CONFIG,
        ...(data.config || {}),
      });
      setAutoRegistrationNextRunTime(data.next_run_time || null);
      setAutoRegistrationMessage('자동등록 설정을 저장했습니다.');
    } catch (err) {
      console.error('자동등록 설정 저장 실패:', err);
      const message = err instanceof Error ? err.message : '자동등록 설정 저장에 실패했습니다.';
      setAutoRegistrationMessage(message);
      alert(message);
    } finally {
      setAutoRegistrationSaving(false);
    }
  };

  const runAutoRegistration = async (dryRun: boolean, retryRun?: AutoRegistrationRun) => {
    const token = localStorage.getItem('token');
    if (!token) return;

    if (!dryRun) {
      const retryLine = retryRun?.source_delivery_date
        ? `\n대상 실패 이력: #${retryRun.id} / ${retryRun.source_delivery_date}`
        : '';
      const shouldRun = window.confirm(
        `자동 차트 등록을 지금 실행하시겠습니까?${retryLine}\n${retryRun ? '재등록은 기존 차트가 있어도 다시 갱신합니다.' : '이미 등록된 날짜는 자동으로 건너뜁니다.'}`
      );
      if (!shouldRun) {
        return;
      }
    }

    setAutoRegistrationExecuting(true);
    setAutoRegistrationMessage(null);
    try {
      const response = await fetch(`${getApiBase()}/api/admin/auto-registration/run`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          force: dryRun || Boolean(retryRun),
          dry_run: dryRun,
        }),
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.detail || '자동등록 실행 실패');
      }

      const isAccepted = data.status === 'accepted';
      const resultMessage = [
        isAccepted
          ? (dryRun ? '점검 실행을 시작했습니다.' : '자동등록 실행을 시작했습니다.')
          : (dryRun ? '점검 실행이 완료되었습니다.' : '자동등록 실행이 완료되었습니다.'),
        data.run_id ? `실행 ID: #${data.run_id}` : null,
        data.target_date ? `대상 날짜: ${data.target_date}` : null,
        data.message || null,
      ].filter(Boolean).join('\n');

      setAutoRegistrationMessage(resultMessage);
      if (isAccepted) {
        setAutoRegistrationIsRunning(true);
      }

      await Promise.all([
        fetchAutoRegistrationConfig(),
        fetchAutoRegistrationRuns(),
      ]);

      if (!dryRun && data.target_date && data.target_date === selectedDate) {
        fetchDashboard();
        fetchDistributionStatus();
      }
    } catch (err) {
      console.error('자동등록 실행 실패:', err);
      const message = err instanceof Error ? err.message : '자동등록 실행에 실패했습니다.';
      setAutoRegistrationMessage(message);
      alert(message);
    } finally {
      setAutoRegistrationExecuting(false);
    }
  };

  const saveDiscordSettings = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;

    setDiscordSaving(true);
    setDiscordMessage(null);
    try {
      const response = await fetch(`${getApiBase()}/api/discord-settings`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(discordSettings),
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.detail || 'Discord 설정 저장 실패');
      }

      setDiscordMessage('Discord 알림 설정을 저장했습니다.');
    } catch (err) {
      console.error('Discord 설정 저장 실패:', err);
      const message = err instanceof Error ? err.message : 'Discord 설정 저장에 실패했습니다.';
      setDiscordMessage(message);
      alert(message);
    } finally {
      setDiscordSaving(false);
    }
  };

  const testDiscordNotification = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;

    setDiscordTesting(true);
    setDiscordMessage(null);
    try {
      const response = await fetch(`${getApiBase()}/api/discord-test`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.detail || 'Discord 테스트 전송 실패');
      }

      setDiscordMessage(data.message || 'Discord 테스트 알림을 전송했습니다.');
    } catch (err) {
      console.error('Discord 테스트 전송 실패:', err);
      const message = err instanceof Error ? err.message : 'Discord 테스트 전송에 실패했습니다.';
      setDiscordMessage(message);
      alert(message);
    } finally {
      setDiscordTesting(false);
    }
  };

  // 메뉴 정렬 순서: 한식 메뉴를 최상단에 배치
  // 실제 데이터 형식에 맞춰 여러 변형 포함
  const menuOrder = [
    '한식 1인', '1인한식', '한식1인',
    '한식 2인', '2인한식', '한식2인',
    '간편식',
    '샐러드',
    '샌드위치'
  ];

  const sortMenuItems = (menuSummary: MenuSummary): [string, number][] => {
    const entries = Object.entries(menuSummary);
    
    // 정렬 로직: 한식 메뉴를 최우선으로
    return entries.sort((a, b) => {
      const menuA = a[0];
      const menuB = b[0];
      
      // 한식 1인/2인을 최우선으로
      const isKorean1A = menuA.includes('한식') && menuA.includes('1');
      const isKorean2A = menuA.includes('한식') && menuA.includes('2');
      const isKorean1B = menuB.includes('한식') && menuB.includes('1');
      const isKorean2B = menuB.includes('한식') && menuB.includes('2');
      
      if (isKorean1A && !isKorean1B) return -1;
      if (!isKorean1A && isKorean1B) return 1;
      if (isKorean1A && isKorean1B) return 0;
      
      if (isKorean2A && !isKorean2B) return -1;
      if (!isKorean2A && isKorean2B) return 1;
      if (isKorean2A && isKorean2B) return 0;
      
      // 한식이 아닌 메뉴들은 menuOrder 순서대로
      const indexA = menuOrder.indexOf(menuA);
      const indexB = menuOrder.indexOf(menuB);
      
      if (indexA !== -1 && indexB !== -1) {
        return indexA - indexB;
      }
      if (indexA !== -1) return -1;
      if (indexB !== -1) return 1;
      
      return menuA.localeCompare(menuB);
    });
  };

  const calculateGrandTotal = (): MenuSummary => {
    if (!dashboardData) return {};
    
    const grandTotal: MenuSummary = {};
    dashboardData.apartments.forEach(apt => {
      Object.entries(apt.menu_summary).forEach(([menu, count]) => {
        grandTotal[menu] = (grandTotal[menu] || 0) + count;
      });
    });
    return grandTotal;
  };

  /**
   * 모든 제거 예약 조회 (관리자용)
   */
  const fetchAllExclusionSchedules = async () => {
    setExclusionLoading(true);
    const token = localStorage.getItem('token');
    if (!token) return;
    
    try {
      const response = await fetch(
        `${getApiBase()}/api/exclusions/list?active_only=false`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setExclusionSchedules(data);
      }
    } catch (err) {
      console.error('제거 예약 목록 조회 실패:', err);
      alert('제거 예약 목록을 불러오는 데 실패했습니다.');
    } finally {
      setExclusionLoading(false);
    }
  };

  /**
   * 제거 예약 삭제
   */
  const deleteExclusionSchedule = async (scheduleId: number) => {
    const token = localStorage.getItem('token');
    if (!token) return;
    
    if (!window.confirm('정말 이 예약을 삭제하시겠습니까?')) return;
    
    try {
      const response = await fetch(
        `${getApiBase()}/api/exclusions/${scheduleId}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      if (response.ok) {
        alert('예약이 삭제되었습니다.');
        fetchAllExclusionSchedules();
      } else {
        const errorData = await response.json();
        alert(`예약 삭제 실패: ${errorData.detail || '알 수 없는 오류'}`);
      }
    } catch (err) {
      console.error('예약 삭제 실패:', err);
      alert('예약 삭제 중 오류가 발생했습니다.');
    }
  };

  /**
   * 제거 예약 관리 모달 열기
   */
  const openExclusionManager = () => {
    setShowExclusionManager(true);
    fetchAllExclusionSchedules();
  };

  const activeAutoRegistrationRun = autoRegistrationRuns.find((run) => run.status === 'running') || null;
  const activeAutoRegistrationProgress = describeAutoRegistrationProgress(activeAutoRegistrationRun);

  useEffect(() => {
    if (!showAutoRegistrationManager) {
      return;
    }

    if (!autoRegistrationIsRunning && !autoRegistrationExecuting && !activeAutoRegistrationRun) {
      return;
    }

    const timer = window.setInterval(() => {
      fetchAutoRegistrationConfig();
      fetchAutoRegistrationRuns();
    }, 5000);

    return () => window.clearInterval(timer);
    // fetch helpers are component-local and intentionally polled from the latest render state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    showAutoRegistrationManager,
    autoRegistrationIsRunning,
    autoRegistrationExecuting,
    activeAutoRegistrationRun?.id,
    activeAutoRegistrationRun?.status,
  ]);


  if (loading) {
    return (
      <div className="admin-dashboard">
        <div className="loading">로딩 중...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-dashboard">
        <div className="error-box">
          <h2>대시보드 로드 실패</h2>
          <p className="error-message">{error}</p>
          <p className="error-hint">해당 날짜에 등록된 배송 차트가 없거나, 네트워크 오류일 수 있습니다.</p>
          <div className="error-actions">
            <button onClick={() => {
              setError(null);
              setSelectedDate(getTomorrow());
              fetchDashboard();
            }} className="retry-button">
              오늘 날짜로 다시 조회
            </button>
            <button onClick={() => {
              localStorage.removeItem('token');
              localStorage.removeItem('driverInfo');
              navigate('/login');
            }} className="logout-button-error">
              로그아웃 후 재로그인
            </button>
          </div>
        </div>
      </div>
    );
  }

  const grandTotal = calculateGrandTotal();
  const sortedGrandTotal = sortMenuItems(grandTotal);
  const totalOrders = Object.values(grandTotal).reduce((sum, count) => sum + count, 0);
  const closeAdminMenu = () => setShowAdminMenu(false);

  const adminMenuSections = [
    {
      title: '차트 작업',
      items: [
        {
          key: 'chart-builder',
          label: '입력/정렬/등록',
          description: '홈페이지 입력, 자동조회, 차트 등록까지 진행합니다.',
          tag: '핵심',
          tone: 'teal',
          onClick: () => {
            closeAdminMenu();
            navigate('/admin/chart-builder');
          },
        },
        {
          key: 'apartments',
          label: '아파트/라인 설정',
          description: 'apt_id, 라인 규칙, 개인배송 대상을 관리합니다.',
          tag: '설정',
          tone: 'violet',
          onClick: () => {
            closeAdminMenu();
            navigate('/admin/apartments');
          },
        },
        {
          key: 'kls-ad-hoc',
          label: 'KLS 개인배송 엑셀',
          description: '개인배송 차트와 임시 전환 대상을 KLS 업로드 양식으로 만듭니다.',
          tag: 'KLS',
          tone: 'blue',
          onClick: () => {
            closeAdminMenu();
            navigate('/admin/kls-ad-hoc');
          },
        },
        {
          key: 'program-config',
          label: 'apartdel 설정 이월',
          description: '기존고객, 보냉가방, 전단지, 개인배송/TOMS 정보를 관리합니다.',
          tag: '이월',
          tone: 'orange',
          onClick: () => {
            closeAdminMenu();
            navigate('/admin/program-config');
          },
        },
      ],
    },
    {
      title: '운영 관리',
      items: [
        {
          key: 'auto-registration',
          label: '자동등록 관리',
          description: '매일 07:00 자동등록, 합계 검증, 알림을 확인합니다.',
          tag: '자동화',
          tone: 'slate',
          onClick: async () => {
            closeAdminMenu();
            await openAutoRegistrationManager();
          },
        },
        {
          key: 'exclusions',
          label: '제거 예약 관리',
          description: '취소 예정 고객과 기간별 제외 내역을 정리합니다.',
          tag: '예약',
          tone: 'orange',
          onClick: () => {
            closeAdminMenu();
            openExclusionManager();
          },
        },
        {
          key: 'start-check',
          label: '출근 확인 현황',
          description: '배송기사 출근 체크와 운영 현황을 새 창에서 확인합니다.',
          tag: '현황',
          tone: 'blue',
          onClick: () => {
            closeAdminMenu();
            window.open('/delivery-start-admin', '_blank');
          },
        },
      ],
    },
    {
      title: '유지 보수',
      items: [
        {
          key: 'cleanup',
          label: '데이터 정리',
          description: '오래된 차트와 운영 데이터를 기준에 따라 정리합니다.',
          tag: '정리',
          tone: 'amber',
          onClick: async () => {
            closeAdminMenu();
            await handleCleanupOldData();
          },
        },
      ],
    },
  ];

  return (
    <div className="admin-dashboard">
      <header className="dashboard-header">
        <div className="header-content">
          <h1>관리자 대시보드</h1>
          <div className="header-right">
            <div className="admin-menu-wrap" ref={adminMenuRef}>
              <button
                type="button"
                className="admin-menu-btn"
                onClick={() => setShowAdminMenu((prev) => !prev)}
              >
                <span className="admin-menu-btn-label">관리 메뉴</span>
                <span className="admin-menu-btn-meta">작업 {adminMenuSections.reduce((count, section) => count + section.items.length, 0)}개</span>
                <span className="admin-menu-btn-arrow">{showAdminMenu ? '▲' : '▼'}</span>
              </button>
              {showAdminMenu && (
                <div className="admin-menu-panel">
                  <div className="admin-menu-panel-header">
                    <div>
                      <strong>관리 작업</strong>
                      <p>자주 쓰는 기능을 목적별로 묶었습니다.</p>
                    </div>
                  </div>

                  {adminMenuSections.map((section) => (
                    <div key={section.title} className="admin-menu-section">
                      <div className="admin-menu-section-title">{section.title}</div>
                      <div className="admin-menu-section-grid">
                        {section.items.map((item) => (
                          <button
                            key={item.key}
                            onClick={item.onClick}
                            className={`admin-menu-card tone-${item.tone}`}
                            type="button"
                          >
                            <span className="admin-menu-card-body">
                              <span className="admin-menu-card-topline">
                                <span className="admin-menu-card-title">{item.label}</span>
                                <span className="admin-menu-chip">{item.tag}</span>
                              </span>
                              <span className="admin-menu-card-description">{item.description}</span>
                            </span>
                            <span className="admin-menu-card-arrow">›</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}

                  <div className="admin-menu-section">
                    <div className="admin-menu-section-title">정렬/세션</div>
                    <label className="admin-menu-toggle-card">
                      <div className="admin-menu-toggle-copy">
                        <span className="admin-menu-card-title">순서 변경 모드</span>
                        <span className="admin-menu-card-description">아파트 카드를 드래그해서 기본 순서를 다시 저장합니다.</span>
                      </div>
                      <input
                        type="checkbox"
                        checked={isDragMode}
                        onChange={(e) => setIsDragMode(e.target.checked)}
                      />
                    </label>
                  {isDragMode && (
                    <button
                      onClick={async () => {
                        closeAdminMenu();
                        await saveAsDefaultOrder();
                      }}
                      className="save-order-btn admin-menu-utility-btn"
                    >
                      기본 순서로 저장
                    </button>
                  )}
                  <button
                    onClick={() => {
                      closeAdminMenu();
                      handleLogout();
                    }}
                    className="logout-button admin-menu-utility-btn"
                  >
                    로그아웃
                  </button>
                  </div>
                </div>
              )}
            </div>
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="date-picker"
            />
          </div>
        </div>
      </header>

      {/* 전체 합계 */}
      <section className="grand-total-section">
        <h2>전체 주문 합계</h2>
        <div className="grand-total-grid">
          {sortedGrandTotal.map(([menu, count]) => (
            <div key={menu} className="grand-total-item">
              <span className="menu-name">{menu}</span>
              <span className="menu-count">{count}개</span>
            </div>
          ))}
          <div className="grand-total-item total">
            <span className="menu-name">총합계</span>
            <span className="menu-count">{totalOrders}개</span>
          </div>
        </div>
      </section>

      {/* 아파트별 요약 */}
      <section className="apartments-section">
        <h2>아파트별 배송 현황 ({dashboardData?.total_apartments || 0}개)</h2>
        {isDragMode && (
          <p className="drag-hint-active">순서 변경 모드 활성화 - 카드를 드래그하여 순서를 바꾸세요</p>
        )}
        
        {!dashboardData || sortedApartments.length === 0 ? (
          <div className="no-data">
            {selectedDate}에 등록된 배송 차트가 없습니다.
          </div>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={sortedApartments.map(apt => apt.chart_id.toString())}
              strategy={verticalListSortingStrategy}
            >
              <div className="apartments-list">
                {sortedApartments.map((apt) => {
                  const sortedMenu = sortMenuItems(apt.menu_summary);
                  const status = distributionStatus[apt.chart_id];
                  return (
                    <SortableApartmentCard
                      key={apt.chart_id}
                      apartment={apt}
                      sortedMenu={sortedMenu}
                      onCardClick={handleApartmentClick}
                      isDragMode={isDragMode}
                      menuChecks={status?.menu_checks || {}}
                      onMenuCheck={handleMenuCheck}
                      isAllDistributed={status?.is_all_distributed || false}
                    />
                  );
                })}
              </div>
            </SortableContext>
          </DndContext>
        )}
      </section>

      {showAutoRegistrationManager && (
        <div className="modal-overlay" onClick={() => setShowAutoRegistrationManager(false)}>
          <div className="modal-content auto-registration-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>자동 차트 등록 관리</h3>
              <button className="close-btn" onClick={() => setShowAutoRegistrationManager(false)}>×</button>
            </div>
            <div className="modal-body">
              {autoRegistrationLoading ? (
                <div className="loading-small">자동등록 정보를 불러오는 중...</div>
              ) : (
                <div className="auto-registration-manager">
                  <div className="auto-registration-status-grid">
                    <div className="auto-registration-status-card">
                      <span className="status-label">상태</span>
                      <strong className={autoRegistrationConfig.enabled ? 'status-enabled' : 'status-disabled'}>
                        {autoRegistrationConfig.enabled ? '활성화' : '비활성화'}
                      </strong>
                    </div>
                    <div className="auto-registration-status-card">
                      <span className="status-label">다음 실행</span>
                      <strong>{autoRegistrationConfig.enabled ? formatDateTime(autoRegistrationNextRunTime) : '비활성화됨'}</strong>
                    </div>
                    <div className="auto-registration-status-card">
                      <span className="status-label">현재 실행</span>
                      <strong>{autoRegistrationIsRunning ? '실행 중' : '대기 중'}</strong>
                    </div>
                  </div>

                  <div className="auto-registration-note">
                    <strong>기준:</strong> carryhealcount 사이트의 다음 조식 날짜와 아파트 수량을 먼저 확인한 뒤,
                    합계가 맞을 때만 전체 아파트 차트 등록을 진행합니다. TOMS/KLS 송장등록은 자동등록에서 제외됩니다.
                  </div>

                  {activeAutoRegistrationRun && (
                    <div className="auto-registration-progress">
                      <span className="status-label">진행 상황</span>
                      <strong>#{activeAutoRegistrationRun.id} {activeAutoRegistrationProgress}</strong>
                    </div>
                  )}

                  <div className="auto-registration-form">
                    <div className="auto-registration-time-row">
                      <label htmlFor="auto-registration-run-time">매일 실행 시각 (한국 시간)</label>
                      <input
                        id="auto-registration-run-time"
                        type="time"
                        value={autoRegistrationConfig.run_time}
                        onChange={(e) => handleAutoRegistrationConfigChange('run_time', e.target.value)}
                        className="date-picker auto-registration-time-input"
                      />
                    </div>

                    <div className="auto-registration-option-grid">
                      <label className="auto-registration-toggle-item">
                        <input
                          type="checkbox"
                          checked={autoRegistrationConfig.enabled}
                          onChange={(e) => handleAutoRegistrationConfigChange('enabled', e.target.checked)}
                        />
                        <span>자동등록 활성화</span>
                      </label>
                      <label className="auto-registration-toggle-item">
                        <input
                          type="checkbox"
                          checked={autoRegistrationConfig.apply_adjustments}
                          onChange={(e) => handleAutoRegistrationConfigChange('apply_adjustments', e.target.checked)}
                        />
                        <span>취소분 자동 반영</span>
                      </label>
                      <label className="auto-registration-toggle-item">
                        <input
                          type="checkbox"
                          checked={autoRegistrationConfig.stop_on_mismatch}
                          onChange={(e) => handleAutoRegistrationConfigChange('stop_on_mismatch', e.target.checked)}
                        />
                        <span>합계 불일치 시 중단</span>
                      </label>
                      <label className="auto-registration-toggle-item">
                        <input
                          type="checkbox"
                          checked={autoRegistrationConfig.notify_on_success}
                          onChange={(e) => handleAutoRegistrationConfigChange('notify_on_success', e.target.checked)}
                        />
                        <span>성공 시 알림</span>
                      </label>
                      <label className="auto-registration-toggle-item">
                        <input
                          type="checkbox"
                          checked={autoRegistrationConfig.notify_on_skip}
                          onChange={(e) => handleAutoRegistrationConfigChange('notify_on_skip', e.target.checked)}
                        />
                        <span>건너뜀 시 알림</span>
                      </label>
                    </div>

                    <div className="auto-registration-source">
                      <span className="status-label">기준 사이트</span>
                      <strong>{autoRegistrationConfig.source_url || AUTO_REGISTRATION_DEFAULT_CONFIG.source_url}</strong>
                    </div>

                    <div className="auto-registration-actions">
                      <button
                        type="button"
                        className="save-order-btn"
                        onClick={saveAutoRegistrationConfig}
                        disabled={autoRegistrationSaving || autoRegistrationExecuting}
                      >
                        {autoRegistrationSaving ? '저장 중...' : '설정 저장'}
                      </button>
                      <button
                        type="button"
                        className="auto-registration-dry-run-btn"
                        onClick={() => runAutoRegistration(true)}
                        disabled={autoRegistrationSaving || autoRegistrationExecuting || autoRegistrationIsRunning}
                      >
                        {autoRegistrationExecuting ? '실행 중...' : '지금 점검 실행'}
                      </button>
                      <button
                        type="button"
                        className="auto-registration-run-btn"
                        onClick={() => runAutoRegistration(false)}
                        disabled={autoRegistrationSaving || autoRegistrationExecuting || autoRegistrationIsRunning}
                      >
                        {autoRegistrationExecuting ? '실행 중...' : '지금 실제 실행'}
                      </button>
                      <button
                        type="button"
                        className="auto-registration-refresh-btn"
                        onClick={async () => {
                          setAutoRegistrationLoading(true);
                          try {
                            await Promise.all([
                              fetchAutoRegistrationConfig(true),
                              fetchAutoRegistrationRuns(true),
                              fetchDiscordSettings(true),
                            ]);
                          } finally {
                            setAutoRegistrationLoading(false);
                          }
                        }}
                        disabled={autoRegistrationSaving || autoRegistrationExecuting}
                      >
                        새로고침
                      </button>
                    </div>

                    {autoRegistrationMessage && (
                      <div className="auto-registration-feedback">
                        {autoRegistrationMessage}
                      </div>
                    )}
                  </div>

                  <div className="auto-registration-runs">
                    <div className="auto-registration-runs-header">
                      <h4>불일치 알림 (Discord)</h4>
                      <span>합계 불일치, 자동등록 실패 시 즉시 전송</span>
                    </div>
                    <div className="auto-registration-form">
                      <label className="auto-registration-toggle-item">
                        <input
                          type="checkbox"
                          checked={discordSettings.enabled}
                          onChange={(e) => setDiscordSettings((prev) => ({
                            ...prev,
                            enabled: e.target.checked,
                          }))}
                        />
                        <span>Discord 알림 사용</span>
                      </label>
                      <div className="auto-registration-webhook-group">
                        <label htmlFor="discord-webhook-url">Discord Webhook URL</label>
                        <input
                          id="discord-webhook-url"
                          type="url"
                          value={discordSettings.webhook_url}
                          onChange={(e) => setDiscordSettings((prev) => ({
                            ...prev,
                            webhook_url: e.target.value,
                          }))}
                          placeholder="https://discord.com/api/webhooks/..."
                          className="auto-registration-webhook-input"
                        />
                      </div>
                      <div className="auto-registration-actions">
                        <button
                          type="button"
                          className="save-order-btn"
                          onClick={saveDiscordSettings}
                          disabled={discordSaving || discordTesting}
                        >
                          {discordSaving ? '저장 중...' : 'Discord 저장'}
                        </button>
                        <button
                          type="button"
                          className="auto-registration-dry-run-btn"
                          onClick={testDiscordNotification}
                          disabled={discordSaving || discordTesting}
                        >
                          {discordTesting ? '전송 중...' : '테스트 알림 보내기'}
                        </button>
                      </div>
                      {discordMessage && (
                        <div className="auto-registration-feedback">
                          {discordMessage}
                        </div>
                      )}
                    </div>

                    <div className="auto-registration-runs-header">
                      <h4>최근 실행 이력</h4>
                      <span>최근 10건</span>
                    </div>
                    {autoRegistrationRuns.length === 0 ? (
                      <div className="auto-registration-empty">실행 이력이 없습니다.</div>
                    ) : (
                      <div className="auto-registration-run-list">
                        {autoRegistrationRuns.map((run) => (
                          <div key={run.id} className="auto-registration-run-item">
                            <div className="auto-registration-run-top">
                              <div>
                                <strong>#{run.id} {run.source_delivery_date || '대상 날짜 미정'}</strong>
                                <div className="auto-registration-run-meta">
                                  {run.trigger_type === 'scheduled' ? '스케줄 실행' : '수동 실행'} · 시작 {formatDateTime(run.started_at)}
                                  {run.finished_at ? ` · 종료 ${formatDateTime(run.finished_at)}` : ''}
                                </div>
                              </div>
                              <div className="auto-registration-run-controls">
                                <span className={`auto-registration-run-status status-${run.status}`}>
                                  {getAutoRegistrationStatusLabel(run.status)}
                                </span>
                                {RETRYABLE_AUTO_REGISTRATION_STATUSES.has(run.status) && (
                                  <button
                                    type="button"
                                    className="auto-registration-retry-btn"
                                    onClick={() => runAutoRegistration(false, run)}
                                    disabled={autoRegistrationExecuting || autoRegistrationIsRunning}
                                    title="carryhealcount 수량을 수정한 뒤 다시 실행하세요."
                                  >
                                    재등록 시작
                                  </button>
                                )}
                              </div>
                            </div>
                            <div className="auto-registration-run-summary">
                              <div>
                                <span className="status-label">기준 합계</span>
                                <strong>{formatSummary(run.source_summary)}</strong>
                              </div>
                              <div>
                                <span className="status-label">실시간 합계</span>
                                <strong>{formatSummary(run.live_summary)}</strong>
                              </div>
                            </div>
                            {run.result_summary && Object.keys(run.result_summary).length > 0 && (
                              <div className="auto-registration-run-result">
                                <span className="status-label">실행 요약</span>
                                <strong>
                                  성공 {Number(run.result_summary.success_count || 0)}개 / 데이터없음 {Number(run.result_summary.no_data_count || 0)}개 / 실패 {Number(run.result_summary.fail_count || 0)}개
                                  {run.result_summary.toms_scheduled_count ? ` / TOMS ${Number(run.result_summary.toms_scheduled_count)}건` : ''}
                                  {run.result_summary.dry_run ? ' / 점검 실행' : ''}
                                </strong>
                              </div>
                            )}
                            {run.status === 'running' && (
                              <div className="auto-registration-run-progress">
                                {describeAutoRegistrationProgress(run)}
                              </div>
                            )}
                            <div className="auto-registration-run-message">{run.message}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 제거 예약 관리 모달 */}
      {showExclusionManager && (
        <div className="modal-overlay" onClick={() => setShowExclusionManager(false)}>
          <div className="modal-content exclusion-manager-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>고객 제거 예약 관리</h3>
              <button className="close-btn" onClick={() => setShowExclusionManager(false)}>×</button>
            </div>
            <div className="modal-body">
              {exclusionLoading ? (
                <div className="loading-small">로딩 중...</div>
              ) : exclusionSchedules.length === 0 ? (
                <p className="no-schedules">등록된 제거 예약이 없습니다.</p>
              ) : (
                <>
                  <div className="exclusion-stats">
                    <span>총 {exclusionSchedules.length}개의 예약</span>
                  </div>
                  <div className="exclusion-schedules">
                    {exclusionSchedules.map(schedule => (
                      <div key={schedule.id} className="schedule-item">
                        <div className="schedule-info">
                          <div className="schedule-apartment">
                            <strong>{schedule.apartment_name}</strong>
                          </div>
                          <div className="schedule-customer">
                            <span className="location">{schedule.dong}동 {schedule.ho}호</span>
                            <span className="customer-name">{schedule.customer_name}</span>
                          </div>
                          <div className="schedule-dates">
                            {schedule.start_date} ~ {schedule.end_date}
                          </div>
                          {schedule.reason && (
                            <div className="schedule-reason">사유: {schedule.reason}</div>
                          )}
                          <div className="schedule-meta">
                            {schedule.created_by && <span>등록자: {schedule.created_by}</span>}
                            {schedule.created_at && <span> | 등록일: {schedule.created_at}</span>}
                          </div>
                        </div>
                        <button
                          className="delete-schedule-btn"
                          onClick={() => deleteExclusionSchedule(schedule.id)}
                        >
                          삭제
                        </button>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
